import { useCallback } from 'react'
import { useStore } from 'react-redux'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { AtlasObjectType, IAtlasObjectRef } from '@common/helpers/atlasUrls'
import { atlasObjectRefKey } from '@common/interfaces/atlasObjects'
import { ROUTES } from '../constants/routes'
import { getAtlasObjectDetail, openAtlasPreview } from '../redux/atlasPreview/actions'
import { useDispatch } from '../redux/hooks'
import { IRootState } from '../redux/reducer'
import { anchorPreview } from './atlasPreviewAnchor'

const isTaskPanelHost = (pathname: string): boolean =>
  !!matchPath(ROUTES.MY_TASKS, pathname) || !!matchPath(ROUTES.TASK_PROJECT_PAGE, pathname)

const hasDetailFor = (state: IRootState, ref: IAtlasObjectRef): boolean => {
  const preview = state.atlasPreview
  if (!preview?.target || atlasObjectRefKey(preview.target.ref) !== atlasObjectRefKey(ref)) {
    return false
  }
  return preview.isLoading || !!preview.detail
}

interface IOpenOptions {
  isPinned?: boolean
}

/**
 * Opens an Atlas record without leaving the page, in a card hanging off `anchor`. Clicking a
 * task on a page that already hosts the task panel opens it there instead, keeping the
 * page's filters. Hovering only shows the card; it never moves to the task panel.
 */
export const useOpenAtlasPreview = () => {
  const dispatch = useDispatch()
  const store = useStore<IRootState>()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  return useCallback(
    (
      ref: IAtlasObjectRef,
      url: string,
      anchor: HTMLElement,
      { isPinned = true }: IOpenOptions = {},
    ) => {
      if (isPinned && ref.type === AtlasObjectType.Task && isTaskPanelHost(pathname)) {
        const params = new URLSearchParams(search)
        params.set('taskId', ref.id)
        void navigate({ pathname, search: params.toString() })
        return
      }

      const isAlreadyLoaded = hasDetailFor(store.getState(), ref)
      anchorPreview(anchor, isPinned)
      dispatch(openAtlasPreview({ ref, url }))
      if (!isAlreadyLoaded) {
        void dispatch(getAtlasObjectDetail(ref))
      }
    },
    [dispatch, navigate, pathname, search, store],
  )
}
